import {useEffect, useMemo, useRef, useState} from "react";
import useTiming from "./useTiming";
import {useRecoilValue} from "recoil";
import {firebaseClaimsState, firebaseUidState, firebaseUserState} from "../state/firebaseState";
import firebaseApp from "../services/firebase";
import firebase from "firebase";

export const useFetchQuestionResults = (itemId) => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(null);
    setLoading(true);

    if(!timing?.id || !firebaseUid) {
      setLoading(false);
      return;
    }

    const cb = (snapshot) => {
      const _data = {};

      snapshot.forEach(item => {
        _data[item.val().value] = (_data[item.val().value] || 0) + 1;
      });

      setData(_data);
      setLoading(false);
    };

    const ref = firebaseApp.database()
    .ref(`room-question-entries/${timing?.id}/${itemId}`);

    ref.on('value', cb)

    return () => {
      setData(null);
      ref.off('value', cb);
    }
  }, [timing?.id, firebaseUid, itemId]);


  return {
    data, loading
  }
}

export const useDeleteMessage = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);

  const [loading, setLoading] = useState(false);

  const mutate = useMemo(() => (key, undelete = false) => {
    setLoading(true);

    if(!timing?.id || !firebaseUid) {
      setLoading(false);
      return;
    }


    firebaseApp.database().ref(`room-messages/${timing?.id}/${key}`)
    .update({
      deletedBy: undelete ? null : firebaseUid,
    }).finally(() => setLoading(false));
  }, [timing?.id, firebaseUid]);

  return {
    loading, mutate
  }
}

export const useTriggerQuestion = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);
  const user = useRecoilValue(firebaseUserState);
  const firebaseClaims = useRecoilValue(firebaseClaimsState);

  const [loading, setLoading] = useState(false);

  const msgRef = useMemo(() => firebaseApp.database().ref(`room-messages/${timing.id}`), [timing?.id, firebaseUid]);

  const mutate = (question) => {
    setLoading(true);

    if(!msgRef) {
      setLoading(false);
      return;
    }

    const messageObj = {
      userId: firebaseUid,
      name: user.name,
      avatar: user?.avatar || null,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      isAdmin: firebaseClaims?.isAdmin || false,
      isOrganiser: firebaseClaims?.isOrganiser || false,
      type:  'question',
      question,
    }
    const newMessage = msgRef.push();

    return newMessage.setWithPriority(messageObj, firebase.database.ServerValue.TIMESTAMP).finally(() => setLoading(false));
  };

  return {
    loading, mutate
  }
}

export const useFetchActiveQuestions = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const timeouts = useRef([]);

  useEffect(() => {
    setData(null);
    setLoading(true);


    if(!timing?.id || !firebaseUid) {
      setLoading(false);
      return;
    }

    const cb = (snapshot) => {
      timeouts.current.forEach(i => clearTimeout(i));
      timeouts.current = [];

      const questions = [];
      snapshot.forEach(i => {
        questions.push({
          id: i.key,
          ...(i.val())
        })
      })
      questions.sort((a,b) => b.timestamp - a.timestamp);

      const recalc = () => {
        const currentDate = +new Date();

        setData(questions.map(i => {
          i.active = currentDate < (i.timestamp + (i.question.duration*1000)) && !i.deletedBy;
          return i;
        }));
      }

      recalc(questions)
      setLoading(false);

      const currentDate = +new Date();

      questions.forEach(item => {
        const when = (item.timestamp + (item.question.duration*1000)) - currentDate;

        if(when > 0) {
          timeouts.current.push(setTimeout(recalc, when))
        }
      });
    };

    const ref = firebaseApp.database().ref(`room-messages/${timing?.id}`)
    .orderByChild('type')
    .equalTo('question');

    ref.on('value', cb)

    return () => {
      ref.off('value', cb);
      timeouts.current.forEach(i => clearTimeout(i));
      timeouts.current = [];
    }
  }, [timing?.id, firebaseUid]);

  return {
    data, loading
  }
}

const useFetchQuestions = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(null);
    setLoading(true);


    if(!timing?.id || !firebaseUid) {
      setLoading(false);
      return;
    }

    const cb = (snapshot) => {
      const questions = [];
      snapshot.forEach(i => {
        questions.push({
          id: i.key,
          ...(i.val())
        })
      })
      questions.sort((a,b) => b.timestamp - a.timestamp);

      setData(questions);
      setLoading(false);
    };

    const ref = firebaseApp.database()
    .ref(`room-questions/${timing?.id}`);

    ref.on('value', cb)

    return () => {
      ref.off('value', cb);
    }
  }, [timing?.id, firebaseUid]);


  return {
    data, loading
  }
}

export default useFetchQuestions;
